import { Field, useFormikContext } from 'formik'
import type { MouseEvent, ReactElement } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import type { FormState } from './shared'
import { getThemePreviewImagePath } from '../../../../utils/themes'
import { useTranslate } from '../../../utils/translate'

export function ThemesListEntry({ theme }: Readonly<{ theme: Core.Theme }>): ReactElement {
  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const hashedThemeAssets = useSelector<State, State>((state) => state.getIn(['view', 'hashedThemeAssets']))
  const { initialValues, values, setFieldValue } = useFormikContext<FormState>()
  const t = useTranslate('interface', 'views.editorThemesView.themesList.themeEntry')
  const previewUrl = usePreviewHref(theme, isBeyondShop)
  const demoUrl = (theme.demoUrls || {})[isBeyondShop ? 'beyond' : 'now']

  const isSelected = theme.id === values.selectedThemeId
  const isActive = theme.id === initialValues.selectedThemeId

  const handleLabelClick = (event: MouseEvent<HTMLLabelElement>) => {
    // deselect the theme if it is already selected
    if (isSelected) {
      event.preventDefault()
      setFieldValue('selectedThemeId', initialValues.selectedThemeId)
    }
  }

  return (
    <div
      role="listitem"
      className={cc([
        'ep-theme-manager-card',
        {
          selected: isSelected,
          active: isActive,
        },
      ])}
    >
      <div className="ep-theme-manager-card-name">{theme.displayName}</div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <label
        className="ep-theme-manager-card-image"
        data-select-hint-text={t('selectHintLabel')}
        onClick={handleLabelClick}
      >
        <Field
          type="radio"
          disabled={isActive || isSelected}
          name="selectedThemeId"
          value={theme.id}
          className="visually-hidden"
        />
        {isActive && (
          <span className="ep-theme-manager-card-badge" aria-hidden="true">
            {t('activeLabel')}
          </span>
        )}
        {!isActive && isSelected && (
          <span className="ep-theme-manager-card-badge" aria-hidden="true">
            {t('selectedLabel')}
          </span>
        )}
        <img alt={theme.displayName} src={getThemePreviewImagePath(theme.name, hashedThemeAssets)} />
      </label>
      <div className="ep-theme-manager-card-cta">
        {demoUrl && (
          <a tabIndex={isSelected ? 0 : -1} href={demoUrl} target="_blank" rel="noopener noreferrer">
            {t('demoShopLink')}
          </a>
        )}
        <a tabIndex={isSelected ? 0 : -1} href={previewUrl} target="_blank" rel="noopener noreferrer">
          {t('previewLink')}
        </a>
      </div>
    </div>
  )
}

function usePreviewHref(theme: Core.Theme, isBeyondShop: boolean) {
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl'], ''))
  const previewUrlPrefix = isBeyondShop
    ? `${mboBaseUrl}/storefront?previewTheme=${theme.name}`
    : `${mboBaseUrl}?ViewAction=UnityMBO-ViewSFThemePreview&previewTheme=${theme.name}`

  return `${previewUrlPrefix}&themeTitle=${encodeURIComponent(theme.displayName)}`
}
